import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function AdminJournalPage() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const isAuth = sessionStorage.getItem('admin_auth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const [articlesRes, categoriesRes] = await Promise.all([
        fetch(`${backendUrl}/api/admin/journal/articles`),
        fetch(`${backendUrl}/api/journal/categories`)
      ]);
      setArticles(await articlesRes.json());
      setCategories(await categoriesRes.json());
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/admin/journal/articles/${id}`, { method: 'DELETE' });
      toast.success('Article deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete article');
    }
  };

  const logout = () => {
    sessionStorage.removeItem('admin_auth');
    navigate('/admin');
  };

  if (loading) return <div className="min-h-screen bg-[hsl(var(--bk-bg))] flex items-center justify-center"><p className="text-[hsl(var(--bk-fg))]">Loading...</p></div>;

  return (
    <div className="min-h-screen bg-[hsl(var(--bk-bg))] p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl text-[hsl(var(--bk-fg))] mb-2">Journal Management</h1>
            <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">BAKLIGHT Admin</p>
          </div>
          <div className="flex gap-4">
            <Link to="/admin/home">
              <Button variant="outline" className="border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">← Back to Admin</Button>
            </Link>
            <Link to="/admin/journal/categories">
              <Button variant="outline" className="border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">Manage Categories</Button>
            </Link>
            <Link to="/admin/journal/new">
              <Button className="bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))]">
                <Plus size={16} className="mr-2" /> New Article
              </Button>
            </Link>
            <Button variant="outline" onClick={logout} className="border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>

        <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-[hsl(var(--bk-line))]">
              <tr>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">TITLE</th>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">CATEGORY</th>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">AUTHOR</th>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">STATUS</th>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">DATE</th>
                <th className="text-right p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {articles.map(article => (
                <tr key={article.id} className="border-b border-[hsl(var(--bk-line))]">
                  <td className="p-4 font-body text-sm text-[hsl(var(--bk-fg))]">{article.title}</td>
                  <td className="p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-muted))]">
                    {categories.find(c => c.id === article.category_id)?.name || 'Uncategorized'}
                  </td>
                  <td className="p-4 font-body text-sm text-[hsl(var(--bk-fg-muted))]">{article.author}</td>
                  <td className="p-4">
                    <span className={`font-mono-meta text-xs px-2 py-1 rounded ${article.status === 'published' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {article.status}
                    </span>
                  </td>
                  <td className="p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-muted))]">
                    {new Date(article.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex gap-2 justify-end">
                      <a href={`/journal/${article.slug}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm"><Eye size={14} /></Button>
                      </a>
                      <Link to={`/admin/journal/edit/${article.id}`}>
                        <Button variant="ghost" size="sm"><Edit size={14} /></Button>
                      </Link>
                      <Button variant="ghost" size="sm" onClick={() => deleteArticle(article.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {articles.length === 0 && (
            <div className="p-12 text-center">
              <p className="font-body text-[hsl(var(--bk-fg-muted))]">No articles yet. Create your first one!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
